import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';

import ModalAddMake from './Modals/ModalAddMake';
import TableMakeCredentials from './components/TableMakeCredentials';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';

import v2_actionDeletarCredencias from 'src/actions/v2/cliente/v2_actionDeletarCredencias';
import v2_actionCredenciais from 'src/actions/v2/cliente/v2_actionCredenciais';
import v2_actionCriarCredenciais from 'src/actions/v2/cliente/v2_actionCriarCredenciais';
import v2_actionEditarCredenciais from 'src/actions/v2/cliente/v2_actionEditarCredenciais';

import { MAKE_DESIGN as d } from './Make.design';

export default function Make() {
    const { t } = useTranslation('settings');
    const { user } = useAuth();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [openWebhook, setOpenWebhook] = useState(false);
    const [nome, setNome] = useState('');
    const [url, setUrl] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [id, setId] = useState(null);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);

    async function getList() {
        try {
            const querys = `?source=make`;
            const response = await v2_actionCredenciais(user.clienteId, querys);
            setList(response.data.data);
        } catch (error) {
            console.log('error', error);
        }
    }

    async function handleCreate() {
        try {
            if (!nome || !url) {
                enqueueSnackbar(t('integrations.make.toast.fillRequiredFields', 'Preencha os campos obrigatórios'), { variant: 'error' });
                return;
            }
            const data = {
                scope: {
                    source: 'make',
                    clienteId: user.clienteId,
                    createdBy: user.id,
                    keys: {
                        name: nome,
                        url: url,
                    },
                },
            };
            const response = await v2_actionCriarCredenciais(user.clienteId, data);
            if (response.status === 200) {
                enqueueSnackbar(t('integrations.make.toast.createSuccess', 'Webhook criado com sucesso'), { variant: 'success' });
                resetAndClose();
                getList();
            }
        } catch (error) {
            console.log('error', error);
            enqueueSnackbar(t('integrations.make.toast.createError', 'Erro ao criar webhook'), { variant: 'error' });
        }
    }

    async function handleEdit() {
        try {
            const data = {
                scope: {
                    keys: {
                        name: nome,
                        url: url,
                    },
                },
            };
            await v2_actionEditarCredenciais(id, data);
            enqueueSnackbar(t('integrations.make.toast.editSuccess', 'Credencial editada com sucesso'), { variant: 'success' });
            resetAndClose();
            getList();
        } catch (error) {
            console.log('error', error);
            enqueueSnackbar(t('integrations.make.toast.editError', 'Erro ao editar credencial'), { variant: 'error' });
        }
    }

    async function handleChangeStatus(id, value) {
        try {
            const data = {
                scope: {
                    status: value,
                    updateAt: new Date(),
                },
            };
            await v2_actionEditarCredenciais(id, data);
            getList();
        } catch (error) {
            console.log('error', error);
        }
    }

    async function handleDeletar() {
        try {
            setLoading(true);
            await v2_actionDeletarCredencias(id);
            await getList();
            setId(null);
            setOpenModalConfirmacao(false);
            enqueueSnackbar(t('integrations.make.toast.deleteSuccess', 'Credencial excluída com sucesso'), { variant: 'success' });
        } catch (error) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    }

    function resetAndClose() {
        setOpenWebhook(false);
        setNome('');
        setUrl('');
        setIsEdit(false);
        setId(null);
    }

    useEffect(() => {
        getList();
    }, []);

    const stats = useMemo(() => {
        const total = list.length;
        const ativos = list.filter((item) => item.status === true).length;
        const inativos = total - ativos;
        return { total, ativos, inativos };
    }, [list]);

    return (
        <div className={d.page.container}>
            {/* Header */}
            <div className={d.page.header.container}>
                <div className={d.page.header.left}>
                    <div className={d.page.header.iconBox}>
                        <Iconify icon="logos:make" width={28} />
                    </div>
                    <div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/dashboard/configuracoes/integracoes')}
                                className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                            >
                                <Iconify icon="eva:arrow-back-outline" width={20} />
                            </button>
                            <h1 className={d.page.header.title}>Make</h1>
                        </div>
                        <p className={d.page.header.subtitle}>{t('integrations.make.header.subtitle', 'Envie eventos para integrar com a plataforma Make')}</p>
                    </div>
                </div>
                <button onClick={() => setOpenWebhook(true)} className={d.page.actionButton}>
                    <Iconify icon="eva:plus-outline" width={18} />
                    <span>{t('integrations.make.createCredentialButton', 'Criar credencial')}</span>
                </button>
            </div>

            {/* Stats */}
            <div className={d.stats.grid}>
                <div className={d.stats.card}>
                    <div className={`${d.stats.iconBox} bg-violet-50`}>
                        <Iconify icon="eva:layers-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <p className={d.stats.value}>{stats.total}</p>
                        <p className={d.stats.label}>{t('labels.total', { ns: 'common' })}</p>
                    </div>
                </div>
                <div className={d.stats.card}>
                    <div className={`${d.stats.iconBox} bg-green-50`}>
                        <Iconify icon="eva:checkmark-circle-2-outline" width={20} className="text-green-600" />
                    </div>
                    <div>
                        <p className={d.stats.value}>{stats.ativos}</p>
                        <p className={d.stats.label}>{t('integrations.make.stats.activeLabel', 'Ativos')}</p>
                    </div>
                </div>
                <div className={d.stats.card}>
                    <div className={`${d.stats.iconBox} bg-gray-100`}>
                        <Iconify icon="eva:close-circle-outline" width={20} className="text-gray-500" />
                    </div>
                    <div>
                        <p className={d.stats.value}>{stats.inativos}</p>
                        <p className={d.stats.label}>{t('integrations.make.stats.inactiveLabel', 'Inativos')}</p>
                    </div>
                </div>
            </div>

            {/* Table */}
            <TableMakeCredentials
                data={list}
                onDelete={(row) => {
                    setId(row._id);
                    setOpenModalConfirmacao(true);
                }}
                onEdit={(row) => {
                    setId(row._id);
                    setIsEdit(true);
                    setNome(row.keys.name);
                    setUrl(row.keys.url);
                    setOpenWebhook(true);
                }}
                changeStatus={(row, value) => handleChangeStatus(row._id, value)}
            />

            {/* Modal Criar/Editar */}
            <ModalAddMake
                isEdit={isEdit}
                openWebhook={openWebhook}
                nome={nome}
                setNome={setNome}
                url={url}
                setUrl={setUrl}
                loading={loading}
                onEdit={handleEdit}
                onCreate={handleCreate}
                onClose={resetAndClose}
            />

            {/* Modal Confirmação Exclusão */}
            <ModalConfimacao
                open={openModalConfirmacao}
                onClose={() => setOpenModalConfirmacao(false)}
                onConfirm={handleDeletar}
                loadingFinalizar={loading}
                mensagem={t('integrations.make.deleteConfirm.message', 'Deseja mesmo excluir esta credencial?')}
            />
        </div>
    );
}
